import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';

const ProfileSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  
  const [profileType, setProfileType] = useState('standard');
  const [profileSettings, setProfileSettings] = useState({
    showLastPosts: true,
    showRegistrationDate: true,
    showGroups: true,
    showWarningStatus: true,
    allowPM: true
  });
  
  const [customFields, setCustomFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Load current profile
      const profileResponse = await axiosInstance.get(`/profile/${user.id}`);
      const profile = profileResponse.data.profile;
      
      setProfileType(profile.profileType || 'standard');
      setProfileSettings(profile.profileSettings || {
        showLastPosts: true,
        showRegistrationDate: true,
        showGroups: true,
        showWarningStatus: true,
        allowPM: true
      });
      
      // Load custom fields
      const fieldsResponse = await axiosInstance.get('/admin/custom-fields/active');
      setAvailableFields(fieldsResponse.data.fields);
      
      // Set current custom field values
      if (profile.customFields) {
        const fieldValues = {};
        profile.customFields.forEach(cf => {
          fieldValues[cf.field._id] = cf.value;
        });
        setCustomFieldValues(fieldValues);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Profil-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      setError(null);
      
      // Prepare custom fields data
      const customFieldsData = availableFields.map(field => ({
        fieldId: field._id,
        value: customFieldValues[field._id] || ''
      })).filter(cf => cf.value.trim() !== '');
      
      const updateData = {
        profileType,
        profileSettings,
        customFields: customFieldsData
      };
      
      await axiosInstance.patch('/profile/settings', updateData);
      setMessage('Profil-Einstellungen erfolgreich gespeichert!');
      
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const renderCustomField = (field) => {
    const value = customFieldValues[field._id] || '';
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className="textarea textarea-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
            maxLength={field.maxLength}
            placeholder={field.description}
            rows={3}
          />
        );
        
      case 'select':
        return (
          <select
            className="select select-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
          >
            <option value="">Bitte wählen...</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        
      case 'date':
        return (
          <input
            type="date"
            className="input input-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
          />
        );
        
      case 'number':
        return (
          <input
            type="number"
            className="input input-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
            min={field.minLength}
            max={field.maxLength}
          />
        );
        
      case 'url':
        return (
          <input
            type="url"
            className="input input-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
            maxLength={field.maxLength}
            placeholder="https://..."
          />
        );
        
      case 'email':
        return (
          <input
            type="email"
            className="input input-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
            maxLength={field.maxLength}
            placeholder="email@beispiel.de"
          />
        );
        
      default: // text
        return (
          <input
            type="text"
            className="input input-bordered w-full"
            value={value}
            onChange={(e) => handleCustomFieldChange(field._id, e.target.value)}
            maxLength={field.maxLength}
            minLength={field.minLength}
            placeholder={field.description}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">👤 Profil-Einstellungen</h1>
        <p className="text-gray-600 mt-2">
          Verwalte deine Profil-Sichtbarkeit und erweiterte Informationen
        </p>
      </div>

      {message && (
        <div className="alert alert-success mb-6">
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error mb-6">
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Type */}
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🎛️ Profil-Typ</h2>
          
          <div className="space-y-3">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="profileType"
                value="standard"
                checked={profileType === 'standard'}
                onChange={(e) => setProfileType(e.target.value)}
                className="radio radio-primary mr-3"
              />
              <span className="font-medium">Standard-Profil</span>
              <p className="text-sm text-gray-600 ml-7">
                Zeigt Grundinformationen wie letzte Beiträge, Registrierungsdatum und Benutzergruppen
              </p>
            </label>
            
            <label className="cursor-pointer">
              <input
                type="radio"
                name="profileType"
                value="extended"
                checked={profileType === 'extended'}
                onChange={(e) => setProfileType(e.target.value)}
                className="radio radio-primary mr-3"
              />
              <span className="font-medium">Erweitertes Profil</span>
              <p className="text-sm text-gray-600 ml-7">
                Zusätzlich zu den Grundinformationen können erweiterte Felder ausgefüllt werden
              </p>
            </label>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="card bg-base-200 shadow-md">
          <div className="card-body">
            <h2 className="card-title">🔒 Sichtbarkeits-Einstellungen</h2>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={profileSettings.showLastPosts}
                onChange={(e) => setProfileSettings(prev => ({
                  ...prev,
                  showLastPosts: e.target.checked
                }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Letzte Beiträge anzeigen</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={profileSettings.showRegistrationDate}
                onChange={(e) => setProfileSettings(prev => ({
                  ...prev,
                  showRegistrationDate: e.target.checked
                }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Registrierungsdatum anzeigen</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={profileSettings.showGroups}
                onChange={(e) => setProfileSettings(prev => ({
                  ...prev,
                  showGroups: e.target.checked
                }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Benutzergruppen anzeigen</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={profileSettings.allowPM}
                onChange={(e) => setProfileSettings(prev => ({
                  ...prev,
                  allowPM: e.target.checked
                }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Private Nachrichten von anderen Benutzern erlauben</span>
            </label>
          </div>
            </div>
          </div>

        {/* Extended Fields */}
        {profileType === 'extended' && availableFields.length > 0 && (
          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <h2 className="card-title">📝 Erweiterte Informationen</h2>
            
            <div className="space-y-4">
              {availableFields.map(field => (
                <div key={field._id}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {renderCustomField(field)}
                  
                  {field.description && (
                    <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                  )}
                </div>
              ))}
            </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#448f65] text-white px-4 py-2 rounded-lg hover:bg-[#2d5f43] transition-colors hover:text-white"
          >
            {saving ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Speichere...
              </>
            ) : (
              '💾 Einstellungen speichern'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
